/**
 * Logique des détours (way in/out), sans dépendance au DOM.
 *
 * Le cœur en est l'ordonnancement des points de passage. L'utilisateur les
 * pose dans l'ordre qui l'arrange — souvent en revenant en arrière pour un
 * point oublié — alors qu'OSRM exige une liste ordonnée. C'est donc
 * l'application qui produit cet ordre.
 */

import { haversine } from './geo'
import type { PolylinePoint } from './polyline'
import type { TrackPoint, WayInOut, WayVia } from './types'

/**
 * Convertit une géométrie routée en points de trace.
 *
 * OSRM ne renvoie que des coordonnées : altitude, horodatage et nom sont
 * absents par nature, et non perdus en chemin.
 */
export function toTrackPoints(
  points: readonly PolylinePoint[],
): TrackPoint[] {
  return points.map((point) => ({
    lat: point.lat,
    lon: point.lon,
    ele: null,
    time: null,
    name: null,
  }))
}

/** Clé de cache de la géométrie d'un détour. */
export function wayCacheKey(wayId: string): string {
  return `way_${wayId}`
}

/**
 * Ordonne les points de passage pour raccourcir le trajet sortie → rentrée.
 *
 * Construction au plus proche voisin depuis la sortie, puis amélioration
 * 2-opt, qui défait les croisements que le glouton laisse derrière lui. Les
 * distances sont à vol d'oiseau : trier sur le réseau routier réel demanderait
 * un appel serveur par permutation, et c'est justement ce que fait le service
 * `trip` d'OSRM quand il est disponible.
 *
 * Exact sur les cas réels — quelques détours presque alignés — et heuristique
 * assumée au-delà : l'itinéraire reste corrigeable point par point sur la
 * carte.
 */
export function orderVias(
  exit: { lat: number; lon: number },
  entry: { lat: number; lon: number },
  vias: readonly WayVia[],
): WayVia[] {
  if (vias.length < 2) return [...vias]

  // --- Plus proche voisin depuis la sortie ---
  const remaining = [...vias]
  const ordered: WayVia[] = []
  let current = exit

  while (remaining.length > 0) {
    let bestIndex = 0
    let bestDistance = Infinity

    for (const [index, via] of remaining.entries()) {
      const distance = haversine(current.lat, current.lon, via.lat, via.lon)
      if (distance < bestDistance) {
        bestDistance = distance
        bestIndex = index
      }
    }

    const [next] = remaining.splice(bestIndex, 1)
    ordered.push(next!)
    current = next!
  }

  // --- 2-opt à extrémités fixes ---
  // Le chemin considéré est [sortie, vias…, rentrée] : les indices 0 et n+1
  // désignent les extrémités, qui ne bougent jamais.
  const at = (index: number): { lat: number; lon: number } => {
    if (index === 0) return exit
    if (index === ordered.length + 1) return entry
    return ordered[index - 1]!
  }

  const between = (a: number, b: number): number => {
    const pa = at(a)
    const pb = at(b)
    return haversine(pa.lat, pa.lon, pb.lat, pb.lon)
  }

  let improved = true
  while (improved) {
    improved = false

    for (let i = 1; i <= ordered.length; i += 1) {
      for (let j = i + 1; j <= ordered.length; j += 1) {
        // Inverser le segment [i..j] remplace les arêtes (i-1, i) et (j, j+1)
        // par (i-1, j) et (i, j+1). Le millimètre de marge évite une boucle
        // infinie sur deux configurations d'égale longueur.
        const gain =
          between(i - 1, i) + between(j, j + 1) -
          (between(i - 1, j) + between(i, j + 1))

        if (gain > 0.001) {
          const segment = ordered.slice(i - 1, j).reverse()
          ordered.splice(i - 1, j - i + 1, ...segment)
          improved = true
        }
      }
    }
  }

  return ordered
}

/**
 * Tolérance sur les points kilométriques, en kilomètres.
 *
 * Le GPX sérialise les distances à six décimales, la persistance locale garde
 * le nombre exact : dix centimètres suffisent à les rapprocher.
 */
const KM_TOLERANCE = 1e-4

/** Tolérance sur les coordonnées d'un point de passage, en degrés. */
const COORD_TOLERANCE = 1e-5

/**
 * Deux détours désignent-ils le même trajet ?
 *
 * Les identifiants ne survivent pas à un passage par le GPX — ils sont
 * régénérés à l'import — alors qu'il faut pouvoir retrouver la géométrie
 * embarquée dans le fichier après une purge du cache. On compare donc la
 * définition plutôt que l'identité.
 */
export function sameWayDefinition(a: WayInOut, b: WayInOut): boolean {
  if (Math.abs(a.exitKm - b.exitKm) > KM_TOLERANCE) return false
  if (Math.abs(a.entryKm - b.entryKm) > KM_TOLERANCE) return false
  if (a.vias.length !== b.vias.length) return false

  return a.vias.every((via, index) => {
    const other = b.vias[index]!
    return (
      Math.abs(via.lat - other.lat) < COORD_TOLERANCE &&
      Math.abs(via.lon - other.lon) < COORD_TOLERANCE
    )
  })
}
